import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, InputAdornment } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { FrmSelectScalarWithHoverHelp } from "components/formFields/FrmSelectScalar";
import { FrmSelectScalarWithObjectOptionsWithHoverHelp } from "components/formFields/FrmSelectScalarWithObjectOptions";
import { FrmTextFieldWithHoverHelp } from "components/formFields/FrmTextFieldWithHoverHelp";
import { FrmAccount } from "components/formFields/frm/FrmAccount";
import { FrmChannel } from "components/formFields/frm/FrmChannel";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { FrmClass } from "components/formFields/frm/FrmClass";
import { FrmDepartment } from "components/formFields/frm/FrmDepartment";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { FrmPaymentMethod } from "components/formFields/frm/FrmPaymentMethod";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { HoverHelp } from "components/utility/HoverHelp";
import { Loading } from "components/utility/Loading";

import { i18n } from "services/i18nService";
import {
  CONNECTION_TYPE_OPTIONS,
  SHOPIFY_TYPE_OPTIONS,
  TRANSACTION_TYPE_OPTIONS,
} from "services/sosInventoryService/cart/schema";
import { getRecord } from "services/sosInventoryService/sosApi";
import { isPersistedRecord } from "services/utility/misc";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { Instructions } from "views/Carts/Cart/Instructions";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function Cart(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [id, dispatch, setRecord, objectType]);

  function getAuthorizationField1Label() {
    switch (record.connectionType) {
      case "Shopify":
        const isPrivate = record.shopifyAppType === "private";
        return isPrivate
          ? i18n("frmLabel.ShopifyApiKey")
          : i18n("frmLabel.AdminApiAccessToken");
      case "BigCommerce":
        return i18n("frmLabel.BigCommerceClientId");
      default:
        break;
    }
  }

  function getAuthorizationField2Label() {
    switch (record.connectionType) {
      case "Shopify":
        return i18n("frmLabel.ShopifyApiPassword");
      case "BigCommerce":
        return i18n("frmLabel.BigCommerceAccessToken");
      default:
        break;
    }
  }

  function getStoreIdentifier() {
    switch (record.connectionType) {
      case "BigCommerce":
        return i18n("frmLabel.BigCommerceApiPath");
      case "PayPal":
        return i18n("frmLabel.PayPalPaymentAddress");
      default:
        break;
    }
  }

  const isPayPal = record?.connectionType === "PayPal";
  const isShopify = record?.connectionType === "Shopify";

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "min-content 1fr",
                      columnGap: "60px",
                      alignItems: "start",
                    }}
                  >
                    <div
                      style={{
                        gridColumn: "1 / 2",
                        maxWidth: "20rem",
                        minWidth: "20rem",
                        gap: "10px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <FrmSelectScalarWithHoverHelp
                        selectProps={{
                          name: "connectionType",
                          label: i18n("frmLabel.Cart"),
                          value: record.connectionType,
                          onValueChange: handleInputFieldChange,
                          options: CONNECTION_TYPE_OPTIONS,
                          disabled: isPersistedRecord(record.id),
                          fullWidth: true,
                        }}
                        helpText={i18n("hoverHelp.cart.connectionType")}
                      />
                      {isShopify && (
                        <FrmSelectScalarWithObjectOptionsWithHoverHelp
                          selectProps={{
                            name: "shopifyAppType",
                            label: i18n("frmLabel.ShopifyAppType"),
                            value: record.shopifyAppType,
                            onValueChange: handleInputFieldChange,
                            options: SHOPIFY_TYPE_OPTIONS,
                            disableClearable: true,
                            fullWidth: true,
                          }}
                          helpText={i18n("hoverHelp.cart.shopifyAppType")}
                        />
                      )}

                      <FrmTextFieldWithHoverHelp
                        name="name"
                        value={record.name}
                        label={i18n("frmLabel.Name")}
                        onValueBlur={handleInputFieldChange}
                        helpText={i18n("hoverHelp.cart.name")}
                        error={isInError("name")}
                        minWidth="20rem"
                      />
                      <FrmResizeTextField
                        name="description"
                        value={record.description}
                        label={i18n("frmLabel.Description")}
                        minRows={2}
                        width="100%"
                        onValueBlur={handleInputFieldChange}
                      />
                      <FrmCheckboxWithHoverHelp
                        name="enabled"
                        value={record.enabled}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.Enabled")}
                        helpText={i18n("hoverHelp.cart.enabled")}
                      />
                      {!isPayPal && (
                        <>
                          <FrmTextFieldWithHoverHelp
                            name="authorizationField1"
                            value={record.authorizationField1}
                            label={getAuthorizationField1Label()}
                            onValueBlur={handleInputFieldChange}
                            helpText={i18n("hoverHelp.cart.followInstructions")}
                            error={isInError("authorizationField1")}
                          />
                          {(!isShopify ||
                            record.shopifyAppType === "private") && (
                            <FrmTextFieldWithHoverHelp
                              name="authorizationField2"
                              value={record.authorizationField2}
                              label={getAuthorizationField2Label()}
                              onValueBlur={handleInputFieldChange}
                              helpText={i18n(
                                "hoverHelp.cart.followInstructions"
                              )}
                              error={isInError("authorizationField2")}
                            />
                          )}
                        </>
                      )}
                      {!isShopify && (
                        <FrmTextFieldWithHoverHelp
                          name="storeIdentifier"
                          value={record.storeIdentifier}
                          label={getStoreIdentifier()}
                          onValueBlur={handleInputFieldChange}
                          helpText={i18n("hoverHelp.cart.followInstructions")}
                          error={isInError("storeIdentifier")}
                        />
                      )}
                      {isShopify && (
                        <>
                          <FrmTextFieldWithHoverHelp
                            name="storeIdentifier"
                            value={record.storeIdentifier}
                            label={i18n("frmLabel.Shop")}
                            onValueBlur={handleInputFieldChange}
                            helpText={i18n("hoverHelp.cart.followInstructions")}
                            endAdornment={
                              <InputAdornment
                                position="end"
                                disableTypography
                                sx={{
                                  lineHeight: "normal",
                                  color: "disabled",
                                  marginRight: 0,
                                }}
                              >
                                .myshopify.com
                              </InputAdornment>
                            }
                            error={isInError("storeIdentifier")}
                          />
                        </>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmChannel
                          fullWidth
                          value={record.channel}
                          onValueChange={handleInputFieldChange}
                          label={i18n("frmLabel.SalesChannel")}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.channel")} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmClass
                          value={record.class}
                          onValueChange={handleInputFieldChange}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.class")} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmDepartment
                          value={record.department}
                          onValueChange={handleInputFieldChange}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.department")} />
                      </div>
                      <FrmSelectScalarWithObjectOptionsWithHoverHelp
                        selectProps={{
                          fullWidth: true,
                          name: "txnType",
                          options: TRANSACTION_TYPE_OPTIONS,
                          label: i18n("frmLabel.TransactionType"),
                          onValueChange: handleInputFieldChange,
                          value: record.txnType,
                          error: isInError("txnType"),
                        }}
                        helpText={i18n("hoverHelp.cart.txnType")}
                      />
                      <FrmAccount
                        name="depositAccount"
                        label={i18n("frmLabel.SalesReceiptDepositAccount")}
                        onValueChange={handleInputFieldChange}
                        value={record.depositAccount}
                        accountType="deposit"
                        helpText={i18n("cart.depositAccount.hoverHelp")}
                      />
                      {isPayPal && (
                        <>
                          <FrmAccount
                            name="incomeAccount"
                            label={i18n("frmLabel.PayPalIncomeAccount")}
                            onValueChange={handleInputFieldChange}
                            value={record.incomeAccount}
                            accountType="income"
                            helpText={i18n("cart.incomeAccount.hoverHelp")}
                          />
                          <FrmAccount
                            name="feeAccount"
                            label={i18n("frmLabel.PayPalFeeAccount")}
                            onValueChange={handleInputFieldChange}
                            value={record.feeAccount}
                            helpText={i18n("cart.feeAccount.hoverHelp")}
                          />
                          <FrmPaymentMethod
                            name="paymentMethod"
                            label={i18n("frmLabel.PaymentMethod")}
                            value={record.paymentMethod}
                            onValueChange={handleInputFieldChange}
                          />
                        </>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmLocation
                          fullWidth
                          value={record.location}
                          onValueChange={handleInputFieldChange}
                          error={isInError("location")}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.location")} />
                      </div>
                      {!isPayPal && (
                        <>
                          <FrmCheckboxWithHoverHelp
                            name="useSosDescription"
                            value={record.useSosDescription}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.OverrideDescription")}
                            helpText={i18n("hoverHelp.cart.useSosDescription")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="useSosOrderNumber"
                            value={record.useSosOrderNumber}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UseSosOrderNumber")}
                            helpText={i18n("hoverHelp.cart.useSosOrderNumber")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="useCommonCustomer"
                            value={record.useCommonCustomer}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UseCommonCustomer")}
                            helpText={i18n("hoverHelp.cart.useCommonCustomer")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="updateInventory"
                            value={record.updateInventory}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UpdateInventory")}
                            helpText={i18n("hoverHelp.cart.updateInventory")}
                          />
                        </>
                      )}
                    </div>
                    <div style={{ gridColumn: "2 / 2" }}>
                      <Instructions
                        connectionType={record.connectionType}
                        shopifyAppType={record.shopifyAppType}
                      />
                    </div>
                  </div>
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
